// THE `usage` PAYLOAD — the token counts a run billed, emitted once, and the bounded
// counter every one of them is built from.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { RunProvenanceSchema } from '../run-provenance';

/**
 * Ceiling on any single token count (#2297). A run that reports a billion tokens
 * on one leg is a broken or hostile box, not a long run: the largest context this
 * platform dials is 1M and the turn budget is in the tens, so the honest upper
 * bound is orders of magnitude below this.
 *
 * It is a REFUSAL bound, not a clamp, and the direction matters. A count past it
 * fails the parse, the whole event is dropped, and the run's spend stays UNKNOWN —
 * which is what the reader must be told. Clamping instead would hand the account
 * spend gate a number nobody measured, and truncating an untrusted count into a
 * column that feeds invoices is how one box's bug bills a tenant.
 */
export const RUN_USAGE_TOKEN_MAX = 1_000_000_000;

/**
 * Ceiling on a row's provider-reported cost, in USD. The same REFUSAL bound as
 * {@link RUN_USAGE_TOKEN_MAX}, and derived from it: that many tokens at $1,000 per 1M —
 * 40x the dearest `MODEL_REGISTRY` rate ($25 per 1M, measured 2026-11-03) — so a figure past it is not a long
 * run but a broken or hostile box, and the row is refused whole.
 */
export const RUN_USAGE_COST_MAX_USD = (RUN_USAGE_TOKEN_MAX / 1_000_000) * 1_000;

/**
 * One optional token count. OPTIONAL IS THE CONTRACT, not a convenience: absent
 * means the provider reported nothing for this leg, and `0` means it reported
 * zero. Collapsing the two is the exact defect this event exists to fix — a PR
 * comment reading `0 tokens · ~$0.00` for work that demonstrably happened.
 */
function usageCount(field: string) {
  return z
    .number()
    .int(`${field}: whole tokens`)
    .nonnegative(`${field}: cannot be negative`)
    .max(RUN_USAGE_TOKEN_MAX, `${field}: implausible token count`)
    .optional();
}

/**
 * The `usage` payload (#2297) — what one run actually spent, reported by the box
 * that spent it. The newest sibling of {@link PlanAuthoredPayloadSchema} /
 * {@link KbLearningsPayloadSchema} / {@link CapabilitiesDetectedPayloadSchema} /
 * {@link ReviewPostedPayloadSchema} / {@link ReportPayloadSchema}, and the closest
 * analogue of `RunProvenanceSchema`: also emitted once, also carrying model facts.
 *
 * WHY IT EXISTS. `agent_sessions.token_in` / `token_out` / `cost_usd` are the only
 * usage columns in the schema, and a fleet parent is INSERTed born-finished with
 * their defaults while the box measured nothing at all — there was no wire slot to
 * report on. So every fleet session in prod records 0 tokens and $0.00, and that $0
 * flows into `sumMonthlySpendUsd`, the account spend gate and the platform MTD
 * overview as if the fleet were free.
 *
 * WHAT IT CARRIES, and why each field. `model` + `provider` are the PRICE BASIS:
 * tokens are meaningless without the id they were spent on, and the fleet parent's
 * own `provider`/`model` columns cannot carry it — they hold the sentinels
 * `'runner'`/`'runner'` that `ensureFleetRunSessionParentInTransaction` re-validates
 * on every read, so writing a model id there would wedge that run's audit chain
 * permanently. The cache and reasoning legs are carried even though today's
 * `MODEL_REGISTRY` prices neither: a cached-read token costs a fraction of a fresh
 * one and a reasoning token bills as output, so a schema that could not represent
 * them would need a migration the first time pricing got sharper. Recording them
 * now costs six integers and buys that for free.
 *
 * They reuse `RunProvenanceSchema`'s own field schemas rather than restating the
 * grammar — one spelling of a token that is rendered VERBATIM into a public PR body
 * (no whitespace, no markdown surface, no autolinkable shape), so a grammar fix
 * lands in both events at once.
 *
 * The parse is the TRUST BOUNDARY the siblings' is: a box is untrusted input and
 * these counts steer a dollar figure on a customer's PR, a spend gate and an
 * invoice input. A malformed payload is refused whole and the run reads as
 * UNMEASURED — never as free.
 */
export const RunUsagePayloadSchema = z.object({
  /** The model id the tokens were spent on — the half of the price basis that names WHAT. */
  model: RunProvenanceSchema.shape.model,
  /** The host that served it, e.g. `api.anthropic.com` / `openrouter.ai`. NOT the wire protocol. */
  provider: RunProvenanceSchema.shape.provider,
  /** Prompt tokens, cached reads included (the SDK counts them in this total). */
  input_tokens: usageCount('input_tokens'),
  /** Completion tokens, reasoning included. */
  output_tokens: usageCount('output_tokens'),
  /** The provider's own total. Kept as reported rather than re-derived — a mismatch is a fact. */
  total_tokens: usageCount('total_tokens'),
  /** Input tokens served from cache — a fraction of the fresh price on every provider that offers it. */
  cache_read_tokens: usageCount('cache_read_tokens'),
  /** Input tokens written INTO the cache — a premium over the fresh price, not a discount. */
  cache_write_tokens: usageCount('cache_write_tokens'),
  /** Output tokens spent thinking. Billed as output today; carried so it need not be inferred. */
  reasoning_tokens: usageCount('reasoning_tokens'),
  /** Model turns the spend covers — the denominator for "cost per turn". */
  steps: z.number().int().nonnegative().max(100_000).optional(),
  /**
   * True when the run DIED before the SDK could report an authoritative total, so
   * these counts are the sum of the steps that finished — a LOWER BOUND. Defaults
   * false so a producer that predates the field reads as complete, and stated at
   * all because a reader totalling spend deserves to know which figures are floors.
   */
  partial: z.boolean().default(false),
  /**
   * The pool key the tokens were billed to (plan 102 slice 01) — the sealed
   * credential's `key_id`, `null` for a `box_env` call or a box predating the field
   * (the same rollout normalisation as `RunProvenanceSchema.key_id`, whose schema this
   * reuses). What makes per-key spend one GROUP BY. Each row is attributed to the
   * credential that SERVED its tokens: a run that billed two keys emits one row per
   * key, so the review lane's sibling fallback key (`llm_credential_fallback`, #2203)
   * gets its own row rather than having its tokens counted against the primary.
   */
  key_id: RunProvenanceSchema.shape.key_id,
  /**
   * What the PROVIDER said these calls cost, in USD (2026-11-03): the sum of every
   * call's own `usage.cost` report (OpenRouter's usage accounting, read through
   * `@developerz/agent` `reported-cost.ts`, the extraction the in-cluster meter uses).
   *
   * Present ONLY when EVERY call the row covers carried a report — the box-side twin of
   * `fullyProviderReported` (`@developerz/agent` `loop/usage.ts`): a sum missing one
   * call's bill is not the row's cost, and a partly-reported row is priced off the card
   * whole instead. A legitimate `0` is a report (the provider vouched the calls were
   * free). Absent means nobody reported; the worker then prices the tokens off the
   * key's card and says so in `price_basis` — the documented basis, never a default.
   *
   * OPTIONAL BOTH WAYS, which is the version-skew contract: a box built before it sends
   * none and is priced exactly as before; a worker built before it strips the key
   * (`z.object` drops unknown keys) and prices the tokens. Bounded like the token
   * counts — a REFUSAL bound, never a clamp: one row past it is a broken or hostile
   * box, and the whole row is dropped rather than an invented figure booked.
   */
  cost_usd: z
    .number()
    .finite('cost_usd: must be finite')
    .nonnegative('cost_usd: cannot be negative')
    .max(RUN_USAGE_COST_MAX_USD, 'cost_usd: implausible cost')
    .optional(),
});
export type RunUsagePayload = z.infer<typeof RunUsagePayloadSchema>;

/**
 * The `usage_ledger` payload (#3881) — the box's own count of what its `usage` rows
 * should add up to, one per run.
 *
 * `passes`: model passes that reached a call attempt — every coding-harness loop, every
 * grounding pass, and a review run's one structured-call pass (plan 101 slice 01),
 * INCLUDING one that died before any step was counted (which publishes no `usage` row
 * and is exactly what this count exists to expose).
 * `usage_rows`: `usage` events whose publish SUCCEEDED. A failed publish is not counted.
 *
 * COMPLETE means `passes === usage_rows` AND the persisted `usage` rows for the run
 * number `usage_rows`. Anything else — including no ledger at all — is an unknown spend,
 * never a total. Both counts are required integers, zero included: `0/0` is a run that
 * drove no model, which is a different fact from a box that predates the event.
 */
export const RunUsageLedgerSchema = z.object({
  passes: z.number().int().nonnegative(),
  usage_rows: z.number().int().nonnegative(),
});
export type RunUsageLedger = z.infer<typeof RunUsageLedgerSchema>;
